import { useWalletBalanceQuery } from '@/src/queries/earnings/wallet-balance'
import { useRouter } from 'next/navigation'
import { useUser } from '@/src/store/user-store'
import {
  Box,
  Text,
  Flex,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Input,
  FormLabel,
  Image,
  InputGroup,
  InputLeftAddon,
  Select,
  Progress,
  Skeleton,
} from '@chakra-ui/react'
import React, { useEffect, useRef, useState } from 'react'

import OtpInput from 'react-otp-input'
import { toast } from 'sonner'
import { useVerificationStatusQuery } from '@/src/queries/earnings/verification-status'

const labelStyles = {
  fontSize: {
    base: 'sm',
    md: 'md',
  },
  mt: {
    base: '1',
    md: '1',
  },
  fontWeight: '500',
  marginBottom: '5px',
  color: '#525252',
}

const inputStyles = {
  size: {
    base: 'md',
  },
  rounded: 'md',
  border: '1px solid #E5E5E5',
  px: {
    base: '3',
    md: '4',
  },
  fontSize: {
    base: 'sm',
    md: 'md',
  },
}

export default function RedeemVerificationEligible({
  isOpen,
  onClose,
  proceedToNext,
  type,
}: {
  isOpen: boolean
  onClose: () => void
  proceedToNext: () => void
  type: string
}) {
  const [step, setStep] = useState<number>()
  const verificationStatus = useVerificationStatusQuery()
  const { user, _hasHydrated } = useUser()
  const timerRef = useRef<NodeJS.Timeout>()
  const walletBalance = useWalletBalanceQuery()
  const router = useRouter()

  const verifyLimit = user?.countryShortname === 'IN' ? 30 : 300
  const redeemLimit = user?.countryShortname === 'IN' ? 100 : 1000
  const verified =
    verificationStatus.data?.panVerification == 'verified' &&
    verificationStatus.data?.bankVerification == 'verified' &&
    user?.mobileotpverify == '1' &&
    _hasHydrated
  useEffect(() => {
    return () => {
      clearTimeout(timerRef.current)
    }
  }, [])

  useEffect(() => {
    if (type === 'verify') {
      setStep(0)
    }
    // verifyAndRedeem
    else if (
      type === 'redeem' &&
      !verified &&
      redeemLimit > Number(walletBalance.data?.verified.lykCoin)
    ) {
      setStep(1)
    }
    // verifiedNotRedeem
    else if (
      type === 'redeem' &&
      verified &&
      redeemLimit + verifyLimit > Number(walletBalance.data?.verified.lykCoin)
    ) {
      setStep(2)
    }
    // verifyToRedeem
    else if (
      type === 'redeem' &&
      !verified &&
      redeemLimit + verifyLimit <= Number(walletBalance.data?.verified.lykCoin)
    ) {
      setStep(3)
    }
  }, [type, user, walletBalance])

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        onClose()
      }}
      size={'md'}
      isCentered
    >
      <ModalOverlay />
      <ModalContent borderRadius={'23px'}>
        <ModalCloseButton mt={2} />

        <ModalBody py={5}>
          <Box mb={5} mt={2} display={'flex'} justifyContent={'center'}>
            <Image
              boxSize="75px"
              src={'/rewards/breakdown/review.svg'}
              alt="wallet"
            />
          </Box>
          {step == 0 ? (
            <>
              <Text
                mb={4}
                textAlign={'center'}
                fontWeight={600}
                fontSize={24}
                opacity={0.7}
              >
                You need{' '}
                {walletBalance.isPending ? (
                  <Skeleton height="24px" my={3} width="24px"></Skeleton>
                ) : (
                  <Text color={'lyk-green'} display={'contents'}>
                    {Number(verifyLimit) -
                      Number(
                        parseFloat(
                          walletBalance.data?.verified.lykCoin || '0'
                        ).toFixed()
                      )}
                  </Text>
                )}{' '}
                more LYK Coins to verify your identity*
              </Text>
              <Progress
                height={'7px'}
                className="progressbarGreen"
                rounded={'full'}
                value={
                  ((Number(
                    parseFloat(
                      walletBalance.data?.verified.lykCoin || '0'
                    ).toFixed()
                  ) /
                    Number(verifyLimit)) *
                    100) |
                  0
                }
                mb={1}
              />
              {walletBalance.isPending ? (
                <Skeleton height="50px" my={3} width="100px"></Skeleton>
              ) : (
                <Box
                  mb={5}
                  mt={2}
                  display={'flex'}
                  justifyContent={'center'}
                  alignItems={'center'}
                >
                  <Image
                    boxSize="35px"
                    src={'/rewards/breakdown/lykcoingreen.svg'}
                    alt="lykcoin"
                    mr={2}
                  />
                  <Text color={'GrayText'} fontWeight={700} fontSize={32}>
                    {parseFloat(
                      walletBalance.data?.verified.lykCoin || '0'
                    ).toFixed()}
                    /
                  </Text>
                  <Text color={'lyk-green'} fontWeight={600} fontSize={32}>
                    {verifyLimit}
                  </Text>
                </Box>
              )}
              <Text fontWeight={500} fontSize={14} textAlign={'center'} mb={6}>
                *The minimum LYK coin balance required for identity verification
                is {verifyLimit} LYK Coins. The verification fee will be
                deducted from your wallet balance
              </Text>

              <Flex justifyContent={'center'} gap={4} mb={4}>
                <Button
                  px={10}
                  bg="lyk-green"
                  color="white"
                  _hover={{ opacity: 0.7 }}
                  size={['sm', 'md', 'md']}
                  fontWeight={500}
                  onClick={() => router.push('/home')}
                >
                  Watch content to earn more
                </Button>
              </Flex>
            </>
          ) : step == 1 ? (
            <>
              <Text
                mb={4}
                textAlign={'center'}
                fontWeight={600}
                fontSize={24}
                opacity={0.7}
              >
                You need{' '}
                {walletBalance.isPending ? (
                  <Skeleton height="24px" my={3} width="24px"></Skeleton>
                ) : (
                  <Text color={'lyk-green'} display={'contents'}>
                    {Number(redeemLimit + verifyLimit) -
                      Number(
                        parseFloat(
                          walletBalance.data?.verified.lykCoin || '0'
                        ).toFixed()
                      )}
                  </Text>
                )}{' '}
                more LYK Coins to verify and redeem*
              </Text>
              <Progress
                height={'7px'}
                className="progressbarGreen"
                rounded={'full'}
                value={
                  ((Number(
                    parseFloat(
                      walletBalance.data?.verified.lykCoin || '0'
                    ).toFixed()
                  ) /
                    Number(redeemLimit + verifyLimit)) *
                    100) |
                  0
                }
                mb={1}
              />
              {walletBalance.isPending ? (
                <Skeleton height="50px" my={3} width="100px"></Skeleton>
              ) : (
                <Box
                  mb={5}
                  mt={2}
                  display={'flex'}
                  justifyContent={'center'}
                  alignItems={'center'}
                >
                  <Image
                    boxSize="35px"
                    src={'/rewards/breakdown/lykcoingreen.svg'}
                    alt="lykcoin"
                    mr={2}
                  />
                  <Text color={'GrayText'} fontWeight={700} fontSize={32}>
                    {parseFloat(
                      walletBalance.data?.verified.lykCoin || '0'
                    ).toFixed()}
                    /
                  </Text>
                  <Text color={'lyk-green'} fontWeight={600} fontSize={32}>
                    {redeemLimit + verifyLimit}
                  </Text>
                </Box>
              )}
              <Text fontWeight={500} fontSize={14} textAlign={'center'} mb={6}>
                *The minimum LYK coin balance required for identity verification
                & redemption is {redeemLimit + verifyLimit} LYK Coins. The
                verification fee will be deducted from your wallet balance{' '}
              </Text>
              <Flex justifyContent={'center'} gap={4} mb={4}>
                <Button
                  px={10}
                  bg="lyk-green"
                  color="white"
                  _hover={{ opacity: 0.7 }}
                  size={['sm', 'md', 'md']}
                  fontWeight={500}
                  onClick={() => router.push('/home')}
                >
                  Watch content to earn more
                </Button>
              </Flex>
            </>
          ) : step == 2 ? (
            <>
              <Text
                mb={4}
                textAlign={'center'}
                fontWeight={600}
                fontSize={24}
                opacity={0.7}
              >
                You need{' '}
                {walletBalance.isPending ? (
                  <Skeleton height="24px" my={3} width="24px"></Skeleton>
                ) : (
                  <Text color={'lyk-green'} display={'contents'}>
                    {Number(redeemLimit+ verifyLimit) -
                      Number(
                        parseFloat(
                          walletBalance.data?.verified.lykCoin || '0'
                        ).toFixed()
                      )}
                  </Text>
                )}{' '}
                more LYK Coins to redeem*
              </Text>
              <Progress
                height={'7px'}
                className="progressbarGreen"
                rounded={'full'}
                value={
                  ((Number(
                    parseFloat(
                      walletBalance.data?.verified.lykCoin || '0'
                    ).toFixed()
                  ) /
                    Number(redeemLimit+ verifyLimit)) *
                    100) |
                  0
                }
                mb={1}
              />
              {walletBalance.isPending ? (
                <Skeleton height="50px" my={3} width="100px"></Skeleton>
              ) : (
                <Box
                  mb={5}
                  mt={2}
                  display={'flex'}
                  justifyContent={'center'}
                  alignItems={'center'}
                >
                  <Image
                    boxSize="35px"
                    src={'/rewards/breakdown/lykcoingreen.svg'}
                    alt="lykcoin"
                    mr={2}
                  />
                  <Text color={'GrayText'} fontWeight={700} fontSize={32}>
                    {parseFloat(
                      walletBalance.data?.verified.lykCoin || '0'
                    ).toFixed()}
                    /
                  </Text>
                  <Text color={'lyk-green'} fontWeight={600} fontSize={32}>
                    {redeemLimit+ verifyLimit}
                  </Text>
                </Box>
              )}
              <Text fontWeight={500} fontSize={14} textAlign={'center'} mb={6}>
                *The minimum LYK coin balance required for redemption is{' '}
                {redeemLimit+ verifyLimit} LYK Coins. A minimal redemption fee will be
                deducted from your wallet.{' '}
              </Text>
              <Flex justifyContent={'center'} gap={4} mb={4}>
                <Button
                  px={10}
                  bg="lyk-green"
                  color="white"
                  _hover={{ opacity: 0.7 }}
                  size={['sm', 'md', 'md']}
                  fontWeight={500}
                  onClick={() => router.push('/home')}
                >
                  Watch content to earn more
                </Button>
              </Flex>
            </>
          ) : step == 3 ? (
            <>
              <Text
                mb={4}
                textAlign={'center'}
                fontWeight={600}
                fontSize={24}
                opacity={0.7}
              >
                You need to verify your identity to redeem LYK Coins.
              </Text>

              <Flex justifyContent={'center'} gap={4} mb={4}>
                <Button
                  px={10}
                  bg="lyk-green"
                  color="white"
                  _hover={{ opacity: 0.7 }}
                  size={['sm', 'md', 'md']}
                  fontWeight={500}
                  onClick={proceedToNext}
                >
                  Continue verification
                </Button>
              </Flex>
            </>
          ) : (
            ''
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}
